// contexts/ContentProtectionContext.js
// ─────────────────────────────────────────────────────────────────────────────
// The institute's content-protection policy, for this browser.
//
//   policy.applies        true only for a student — the server decides that
//   policy.blockCopy      LMS-wide: no selecting / copying / dragging text
//   policy.blockDevTools  LMS-wide: block inspector shortcuts, hide the page
//                         while developer tools are open
//   policy.fileAccess     the institute's default level for new uploads
//   policy.enforceFileAccess  …and whether that default is a floor for every file
//
// Rules: vc_be/utils/contentAccess.js. Per-FILE access never comes from here —
// every material and lesson carries its own resolved `access` block, because
// only the server knows a file's level.
//
// The last answer is cached per user so a reload is protected from the first
// paint instead of from whenever the request lands. Editing the cache buys a
// student a few hundred milliseconds: the fetch overwrites it.
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import apiClient from '../utils/apiClient';
import { getCurrentUser } from '../utils/auth';

export const POLICY_OFF = Object.freeze({
  applies: false,
  blockCopy: false,
  blockDevTools: false,
  fileAccess: 'download',
  enforceFileAccess: false,
});

const ContentProtectionContext = createContext({ policy: POLICY_OFF, refresh: () => {} });

const userKey = (user) => {
  const id = user?.id || user?._id || user?.userId;
  return id ? `vc-content-protection:${id}` : null;
};

const readCache = (user) => {
  const key = userKey(user);
  if (!key) return POLICY_OFF;
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...POLICY_OFF, ...JSON.parse(raw) } : POLICY_OFF;
  } catch {
    return POLICY_OFF;
  }
};

const writeCache = (user, policy) => {
  const key = userKey(user);
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify(policy)); } catch { /* private mode */ }
};

export function ContentProtectionProvider({ children }) {
  const [policy, setPolicy] = useState(() => readCache(getCurrentUser()));

  const refresh = useCallback(async () => {
    const user = getCurrentUser();
    // Signed out, or the platform operator: nothing to enforce, nothing to ask.
    if (!user || user.usertype === 'tenantadmin') {
      setPolicy(POLICY_OFF);
      return;
    }
    setPolicy(readCache(user));
    try {
      const { data } = await apiClient.get('/institute/content-protection');
      const next = { ...POLICY_OFF, ...(data?.data || {}) };
      writeCache(user, next);
      setPolicy(next);
    } catch {
      // Keep the cached answer. Failing OPEN here would let a student switch
      // every protection off by blocking one request.
    }
  }, []);

  useEffect(() => {
    refresh();
    // Login and logout are SPA navigations; this is the event they announce.
    window.addEventListener('vc-auth-change', refresh);
    return () => window.removeEventListener('vc-auth-change', refresh);
  }, [refresh]);

  const value = useMemo(() => ({ policy, refresh }), [policy, refresh]);
  return <ContentProtectionContext.Provider value={value}>{children}</ContentProtectionContext.Provider>;
}

export const useContentProtection = () => useContext(ContentProtectionContext);
